import React, { Fragment, useCallback, useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Typography from '@mui/material/Typography'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import AutocompleteChip from '../components/AutocompleteChip'
import AutocompleteInput from '../components/AutocompleteInput'
import styled from 'styled-components'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import { checkIfArrayIsUnique } from '../utils'
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';

const VerticalSpacer = styled.div`
  height: 8px;
`

const AddMeta = styled(Button)`
  cursor: pointer;
  margin-top: 8px;
`

const MetaContainer = styled.div`
  display: flex;
  gap: 8px;
`

const AddMetaContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: end;
`

const AssetConfig = ({ open, onClose, onSave, asset, tags, types }) => {
  const [selectedTags, setSelectedTags] = useState([])
  const [selectedType, setSelectedType] = useState('')
  const [selectedMeta, setSelectedMeta] = useState([])

  useEffect(() => {
    setSelectedTags(asset?.tags ?? [])
  }, [asset?.tags])

  useEffect(() => {
    setSelectedType(asset?.type ?? '')
  }, [asset?.type])

  useEffect(() => {
    if (asset?.metadata) {
      const currentMeta = []
      for (const [key, value] of Object.entries(asset?.metadata)) {
        currentMeta.push({ key, value })
      }
      setSelectedMeta(currentMeta)
    } else {
      setSelectedMeta([])
    }
  }, [asset?.metadata])

  const handleSave = useCallback(() => {
    const metaKeys = selectedMeta.map(m => m.key)
    const areKeysUnique = checkIfArrayIsUnique(metaKeys)

    if (areKeysUnique) {
      let metadata = {}
      selectedMeta.forEach((m) => {
        if (m.key && m.value) {
          metadata = { ...metadata, [m.key]: m.value }
        }
      })
      onSave(selectedTags, selectedType, metadata, asset?.asset_id)
      onClose()
    } else {
      alert('Please use unique meta keys')
    }
  }, [asset?.asset_id, onClose, onSave, selectedMeta, selectedTags, selectedType])

  const handleAddMeta = useCallback(() => {
    setSelectedMeta(currentMeta => ([...currentMeta, { key: '', value: '' }]))
  }, [])

  const handleDeleteMeta = useCallback((index) => {
    setSelectedMeta(currentMeta => currentMeta.filter((_, i) => i !== index))
  }, [])

  const handleMetaChange = useCallback((index, attribute, value) => {
    setSelectedMeta(currentMeta => currentMeta.map((m, i) => {
      if (i === index) {
        return {
          ...m,
          [attribute]: attribute === 'value' ? value : value.replace(' ', '_'),
        }
      }
      return m
    }))
  }, [])

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Asset {asset?.asset_id}</DialogTitle>
      <DialogContent>
        <AutocompleteChip label='Tags' options={tags} onChange={setSelectedTags} value={selectedTags} placeholder='Select the asset tags' />
        <VerticalSpacer />
        <AutocompleteInput label='Types' options={types} value={selectedType} onChange={setSelectedType} placeholder='Write the asset type' />
        <VerticalSpacer />
        <Typography>Meta</Typography>
        <VerticalSpacer />
        {selectedMeta.length === 0 && (
          <Typography>There are no meta for this asset</Typography>
        )}
        {selectedMeta.map((m, i) => (
          <Fragment key={i}>
            <MetaContainer>
              <TextField value={m.key} onChange={(e) => handleMetaChange(i, 'key', e.target.value)} label='Key' />
              <TextField value={m.value} onChange={(e) => handleMetaChange(i, 'value', e.target.value)} label='Value' />
              <IconButton aria-label="delete" onClick={() => handleDeleteMeta(i)}>
                <DeleteIcon />
              </IconButton>
            </MetaContainer>
            <VerticalSpacer />
          </Fragment>
        ))}
        <VerticalSpacer />
        <AddMetaContainer>
          <AddMeta onClick={handleAddMeta} variant="outlined" startIcon={<AddBoxOutlinedIcon />}>Add Meta</AddMeta>
        </AddMetaContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export default AssetConfig
